(ns hai.home.calendar
  "Google Calendar state, layout, and accessors for the home dashboard's flow +
   meetings cards. The card rendering lives in each mini-app namespace."
  (:require
   [hai.home.clock :as clock]
   [hai.journal2.events :as j2-events]
   [hai.tasks.data :as tasks-data]
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]))

;; ---------------------------------------------------------------------------
;; Helpers
;; ---------------------------------------------------------------------------

(defn- start-of-today []
  (doto (js/Date.) (.setHours 0 0 0 0)))

(defn- end-of-today []
  (doto (js/Date.) (.setHours 23 59 59 999)))

(defn- local-day-key
  "Local-wall-clock day identifier. Used to detect midnight rollover while
   the page is left open."
  []
  (let [d (js/Date.)]
    (str (.getFullYear d) "-" (inc (.getMonth d)) "-" (.getDate d))))

(def ^:private rolling-refresh-ms (* 3 60 60 1000))

;; Polymorphic meeting-field accessors — a meeting entry may be a Google Calendar
;; or an Outlook Calendar journal entry, which carry parallel but differently
;; namespaced attributes. Read either.
(defn m-end-at [m]
  (or (:journal.entry.google-calendar/end-at m) (:journal.entry.outlook-calendar/end-at m)))
(defn m-title [m]
  (or (:journal.entry.google-calendar/title m) (:journal.entry.outlook-calendar/title m)))
(defn m-attendees [m]
  (or (:journal.entry.google-calendar/attendees m) (:journal.entry.outlook-calendar/attendees m)))
(defn m-attendee-names [m]
  (or (:journal.entry.google-calendar/attendee-names m) (:journal.entry.outlook-calendar/attendee-names m)))
(defn m-meet-link [m]
  (or (:journal.entry.google-calendar/meet-link m) (:journal.entry.outlook-calendar/meet-link m)))
(defn m-meet-label
  "Provider name for a meeting's join link: Google Meet for gcal, Teams for Outlook."
  [m]
  (cond
    (:journal.entry.google-calendar/meet-link m) "Google Meet"
    (:journal.entry.outlook-calendar/meet-link m) "Teams"))
(defn m-link [m]
  (or (:journal.entry.google-calendar/link m) (:journal.entry.outlook-calendar/link m)))

(defn- classify-meeting
  "Classify a calendar entry as :past, :now, :next, or :upcoming relative to `now-ms`.
   `first-future-seen?` is an atom to track whether the :next slot has been assigned."
  [entry now-ms first-future-seen?]
  (let [start-ms (.getTime (js/Date. (:journal.entry/time entry)))
        end-at   (m-end-at entry)
        ;; Fall back to 24h so an entry without an end can't latch into :now.
        end-ms   (if end-at
                   (.getTime (js/Date. end-at))
                   (+ start-ms (* 24 60 60 1000)))]
    (cond
      (< end-ms now-ms)    :past
      (<= start-ms now-ms) :now
      (not @first-future-seen?)
      (do (reset! first-future-seen? true) :next)
      :else :upcoming)))

(defn- sort-by-start [entries]
  (->> entries
       (filter :journal.entry/time)
       (sort-by #(.getTime (js/Date. (:journal.entry/time %))))
       vec))

(defn- classify-meetings [entries now-ms]
  (let [seen? (atom false)]
    (mapv (fn [e]
            (assoc e ::status (classify-meeting e now-ms seen?)))
          entries)))

(defn calendar-week-url [calendar-type]
  (case calendar-type
    :google "https://calendar.google.com/calendar/u/0/r/week"
    ; Personal (Microsoft account)
    :microsoft-personal "https://outlook.live.com/calendar/view/week"
    ; Work / School (Microsoft 365 / Entra ID)
    :microsoft-work "https://outlook.office.com/calendar/view/week"
    ; sad noises, week view is not URL-addressable for those below
    :fastmail "https://app.fastmail.com/calendar/"
    :tuta "https://app.tuta.com/calendar"
    :proton "https://calendar.proton.me"
    :icloud "https://www.icloud.com/calendar"))

;; ---------------------------------------------------------------------------
;; Stack lanes (greedy interval-graph coloring)
;; ---------------------------------------------------------------------------

(defn- meeting-end-ms [m]
  (let [start-ms (.getTime (js/Date. (:journal.entry/time m)))
        end-at   (m-end-at m)]
    (if end-at
      (.getTime (js/Date. end-at))
      (+ start-ms (* 30 60000)))))

(defn- assign-lanes
  "Greedy interval-graph coloring. Returns `{uuid {:lane n :overlapping? bool}}`.
   Sequential, non-overlapping meetings always land in lane 0 with
   `:overlapping? false`. When intervals intersect, both the new meeting and
   the still-active ones it joins get flagged `:overlapping? true`."
  [meetings]
  (let [sorted (sort-by #(.getTime (js/Date. (:journal.entry/time %))) meetings)]
    (loop [ms sorted active [] out {}]
      (if (empty? ms)
        out
        (let [m            (first ms)
              uuid         (:journal.entry/uuid m)
              start-ms     (.getTime (js/Date. (:journal.entry/time m)))
              end-ms       (meeting-end-ms m)
              still-active (filterv #(> (:end-ms %) start-ms) active)
              overlap?     (seq still-active)
              used-lanes   (into #{} (map :lane) still-active)
              lane         (first (remove used-lanes (range)))
              out'         (cond-> (assoc out uuid {:lane lane :overlapping? (boolean overlap?)})
                             overlap? (as-> $ (reduce (fn [acc {:keys [uuid]}]
                                                        (assoc-in acc [uuid :overlapping?] true))
                                                      $
                                                      still-active)))]
          (recur (rest ms)
                 (conj still-active {:uuid uuid :end-ms end-ms :lane lane})
                 out'))))))

;; ---------------------------------------------------------------------------
;; Cross-axis lane layout (shared by desktop + mobile flow renderers)
;; ---------------------------------------------------------------------------
;; The time axis is the "main" axis (X on desktop, Y on mobile). This computes
;; placement on the free "cross" axis (Y on desktop, X on mobile), normalized
;; 0..1: timed events fill the whole band, split into halves/thirds only when
;; overlaps require it. All-day events get no lane — the progress cards render
;; them as "All day" pills in the subtitle row (birthdays alone can pile up
;; several deep, which made a dedicated band unusable).

(def ^:private all-day-threshold-ms (* 24 60 60 1000))

(defn all-day?
  "An entry is all-day when it spans >= 24h. Matches the backend convention
   (start at 08:00Z, end-at = start + 24h) without timezone-fragile parsing."
  [m]
  (when-let [ea (m-end-at m)]
    (>= (- (.getTime (js/Date. ea))
           (.getTime (js/Date. (:journal.entry/time m))))
        all-day-threshold-ms)))

(defn- split-count
  "Number of cross-axis lanes: 1 (no overlap) / 2 (1-way) / 3 (2-way+, capped).
   Derived from the greedy lane map's max lane index."
  [lane-map]
  (min 3 (inc (apply max 0 (map :lane (vals lane-map))))))

(defn compute-lane-layout
  "Shared pure layout: maps each timed meeting uuid to a cross-axis placement
   record {:lane :overlapping? :cross-start :cross-size :split-count}. Lanes
   beyond the cap reuse the last lane (opacity stacking handles overflow).
   All-day events get no record — they are not drawn on the band."
  [meetings]
  (let [timed     (remove all-day? meetings)
        lane-map  (assign-lanes timed)
        splits    (split-count lane-map)
        lane-size (/ 1 splits)]
    (reduce (fn [acc m]
              (let [uuid (:journal.entry/uuid m)
                    {:keys [lane overlapping?]} (get lane-map uuid)
                    eff-lane (min lane (dec splits))]
                (assoc acc uuid
                       {:lane         lane
                        :overlapping? overlapping?
                        :cross-start  (* eff-lane lane-size)
                        :cross-size   lane-size
                        :split-count  splits})))
            {}
            timed)))

;; ---------------------------------------------------------------------------
;; Example preview (shown dimmed when the user has no GCal connection)
;; ---------------------------------------------------------------------------

(defn- at
  "Today at the given wall-clock hour:minute."
  [hour minute]
  (doto (js/Date.) (.setHours hour minute 0 0)))

(defn sample-meetings
  "A synthetic, plausible day. Statuses are fixed (not derived from the clock)
   so the preview always reads well: a finished standup, a live design review,
   an upcoming 1:1, and two later blocks."
  []
  [{:journal.entry/uuid "sample-standup"
    :journal.entry/time (at 9 30)
    :journal.entry.google-calendar/end-at (at 9 45)
    :journal.entry.google-calendar/title "Morning standup"
    ::status :past
    :journal.entry.google-calendar/attendees ["maya@acme.com" "leo@acme.com" "raj@acme.com" "ana@acme.com"]
    :journal.entry.google-calendar/attendee-names ["Maya Chen" "Leo Park" "Raj Patel" "Ana Díaz"]}
   {:journal.entry/uuid "sample-design"
    :journal.entry/time (at 11 0)
    :journal.entry.google-calendar/end-at (at 11 45)
    :journal.entry.google-calendar/title "Design review — Mobile"
    ::status :now
    :journal.entry.google-calendar/meet-link "https://meet.google.com/example"
    :journal.entry.google-calendar/attendees ["maya@acme.com" "jordan@acme.com" "sam@acme.com"]
    :journal.entry.google-calendar/attendee-names ["Maya Chen" "Jordan Lee" "Sam Rivera"]}
   {:journal.entry/uuid "sample-oneonone"
    :journal.entry/time (at 13 0)
    :journal.entry.google-calendar/end-at (at 13 30)
    :journal.entry.google-calendar/title "1:1 — Priya"
    ::status :next
    :journal.entry.google-calendar/meet-link "https://meet.google.com/example"
    :journal.entry.google-calendar/attendees ["priya@acme.com"]
    :journal.entry.google-calendar/attendee-names ["Priya Nair"]}
   {:journal.entry/uuid "sample-roadmap"
    :journal.entry/time (at 15 0)
    :journal.entry.google-calendar/end-at (at 16 0)
    :journal.entry.google-calendar/title "Q3 roadmap planning"
    ::status :upcoming
    :journal.entry.google-calendar/attendees ["maya@acme.com" "leo@acme.com" "jordan@acme.com"
                                              "raj@acme.com" "ana@acme.com" "sam@acme.com"]
    :journal.entry.google-calendar/attendee-names ["Maya Chen" "Leo Park" "Jordan Lee"
                                                   "Raj Patel" "Ana Díaz" "Sam Rivera"]}
   {:journal.entry/uuid "sample-customer"
    :journal.entry/time (at 17 0)
    :journal.entry.google-calendar/end-at (at 17 30)
    :journal.entry.google-calendar/title "Customer call — Northwind"
    ::status :upcoming
    :journal.entry.google-calendar/meet-link "https://meet.google.com/example"
    :journal.entry.google-calendar/attendees ["dana@northwind.com" "leo@acme.com" "maya@acme.com"]
    :journal.entry.google-calendar/attendee-names ["Dana Brooks" "Leo Park" "Maya Chen"]}])

;; ---------------------------------------------------------------------------
;; Events
;; ---------------------------------------------------------------------------

(defevent-fx ::init [{:keys [db]} _]
  {:db (assoc db ::loading? true)
   :fx [[:dispatch [::load-events]]
        [:dispatch [::refresh-gcal]]
        [:dispatch [::refresh-outlook]]
        ;; Populate :timeline/connection-status so the home-page Connect
        ;; button can use timeline-page branching logic.
        [:dispatch [:timeline/load-connection-status]]]})

(defevent-fx ::refresh-gcal [_ _]
  {:resolve {:query [{(list 'integration/refresh-gcal {})
                      [:refresh-required?]}]
             :store-in [::refresh-result]
             :transform-fn (fn [resp] (get resp 'integration/refresh-gcal))
             :on-success-fx [[:dispatch [::refresh-done]]]}})

(defevent-fx ::refresh-outlook [_ _]
  {:resolve {:query [{(list 'integration/refresh-outlook-calendar {})
                      [:refresh-required?]}]
             :store-in [::refresh-result]
             :transform-fn (fn [resp] (get resp 'integration/refresh-outlook-calendar))
             :on-success-fx [[:dispatch [::refresh-done]]]}})

(defevent-fx ::refresh-done [{:keys [db]} _]
  (let [result   (get db ::refresh-result)
        refresh? (:refresh-required? result)]
    (cond-> {:db (dissoc db ::refresh-result)}
      refresh? (assoc :fx [[:dispatch [::load-events]]]))))

(defevent-fx ::load-events [{:keys [db]} _]
  ;; Cancel prior in-flight so a midnight-rollover re-dispatch can't have a
  ;; stale response clobber the new one.
  (let [prev-id (::request-id db)
        req-id  (random-uuid)
        since   (start-of-today)
        until   (end-of-today)]
    {:db (cond-> (-> db
                     (dissoc ::error?)
                     (assoc ::request-id req-id))
           prev-id (update :resolve/canceled-requests (fnil conj #{}) prev-id))
     :resolve {:request-id req-id
               :query [{(list :integration/entry-view
                              {:selected-integrations nil
                               :filters {:page {:shown-types
                                                #{:timeline-integration.type/google-calendar
                                                  :timeline-integration.type/outlook-calendar}
                                                :date-range {:since since
                                                             :until until}}}})
                        (j2-events/journal-entry-query)}]
               :store-in [::entries-raw]
               :transform-fn (fn [resp] (get resp :integration/entry-view []))
               :on-success-fx [[:dispatch [::events-loaded]]]
               :on-error-fx [[:dispatch [::load-error]]]}}))

(defevent ::events-loaded [db _]
  (-> db
      (assoc ::entries (sort-by-start (get db ::entries-raw []))
             ::loading? false
             ::day-key (local-day-key)
             ::last-refresh-ms (.getTime (js/Date.)))
      (dissoc ::entries-raw ::request-id)))

(defevent ::load-error [db _]
  (assoc db ::loading? false
         ::error? true))

(defevent-fx ::tick [{:keys [db]} _]
  (let [day-key      (local-day-key)
        prior-key    (::day-key db)
        last-refresh (::last-refresh-ms db 0)
        now-ms       (.getTime (js/Date.))
        rolled-over? (and prior-key (not= prior-key day-key))
        rolling?     (and (pos? last-refresh)
                          (>= (- now-ms last-refresh) rolling-refresh-ms))]
    ;; Datomic is not rate-limited — re-grab full local day window on either
    ;; trigger. GCal API sweep is gated to the 3h cadence (forward/back
    ;; logic lives server-side in `integration/refresh-gcal`).
    {:fx (cond-> []
           (or rolled-over? rolling?) (conj [:dispatch [::load-events]])
           ;; Tasks are day-scoped too (completions since midnight)
           ;; — re-fetch so yesterday's stamps drop off the flow.
           rolled-over?               (conj [:dispatch [::tasks-data/load-owned-or-assigned-tasks]])
           rolling?                   (conj [:dispatch [::refresh-gcal]])
           rolling?                   (conj [:dispatch [::refresh-outlook]]))}))

(defevent ::hover-meeting [db uuid]
  (assoc db ::hovered-uuid uuid))

(defevent ::teardown [db _]
  (dissoc db ::loading? ::error? ::entries
          ::day-key ::last-refresh-ms ::hovered-uuid
          ::entries-raw ::refresh-result ::request-id))

;; ---------------------------------------------------------------------------
;; Subscriptions
;; ---------------------------------------------------------------------------

(defsub ::loading? [db]
  (get db ::loading? false))

;; nil means the fetch hasn't returned yet; {} means it has (even with no connections).
(defsub ::connection-status-initialized? [db]
  (some? (get db :timeline/connection-status)))

(defsub ::entries [db]
  (get db ::entries []))

(defsub ::meetings
  [entries <- ::entries
   now-ms  <- ::clock/now-ms]
  (if now-ms
    (classify-meetings entries now-ms)
    []))

;; Timed/all-day partitions of today's meetings. The cards draw timed events on
;; the time axis; all-day events (birthdays, OOO, …) have no meaningful span
;; there and surface as "All day" pills instead.
(defsub ::timed-meetings
  [meetings <- [::meetings]]
  (vec (remove all-day? meetings)))

(defsub ::all-day-meetings
  [meetings <- [::meetings]]
  (filterv all-day? meetings))

(defsub ::meeting-layout
  [meetings <- [::meetings]]
  (compute-lane-layout meetings))

(defsub ::error? [db]
  (get db ::error? false))

;; True iff the user has a google-calendar timeline-integration AND a live OAuth
;; connection. Drives whether the meetings panel shows real data, or the Connect
;; placeholder. Either side falsey (TI deleted, OAuth expired) surfaces the
;; Connect affordance.
(defsub ::gcal-active? [db]
  (let [status (get-in db [:timeline/connection-status
                           :timeline-integration.type/google-calendar])]
    (boolean (and (:enabled? status) (:connected? status)))))

(defsub ::outlook-cal-active? [db]
  (let [status (get-in db [:timeline/connection-status
                           :timeline-integration.type/outlook-calendar])]
    (boolean (and (:enabled? status) (:connected? status)))))

;; True iff EITHER calendar provider is connected & enabled. Drives the meetings
;; panel real-data-vs-Connect-placeholder branching.
(defsub ::any-cal-active?
  [gcal? <- [::gcal-active?]
   outlook? <- [::outlook-cal-active?]]
  (or gcal? outlook?))

;; Calendar connections (either provider) whose credential is invalid —
;; drives the "Connection broken, reconnect" badge. Distinct from
;; `::any-cal-active?`: a user can have one healthy calendar AND one broken
;; one at the same time (multi-calendar), so both surfaces can be shown.
(defsub ::broken-connections [db]
  (let [status (:timeline/connection-status db)
        tagged (fn [integration-type]
                 (->> (get-in status [integration-type :connections])
                      (filter :invalid?)
                      (map #(assoc % :integration-type integration-type))))]
    (into (tagged :timeline-integration.type/google-calendar)
          (tagged :timeline-integration.type/outlook-calendar))))

;; Per-meeting hover flag rather than one hovered-uuid sub: only the rows/blocks
;; whose answer flips re-render on a hover change, instead of every subscriber.
(defsub ::meeting-hovered? [db meeting-uuid]
  (= meeting-uuid (get db ::hovered-uuid)))
